import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import '../styles/AdminSettingsPage.css';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import HorizonManagementModal from '../components/HorizonManagementModal';
import { GoogleCredentialsPanel } from '../components/GoogleCredentialsPanel';

function AdminSettingsPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteUserType, setInviteUserType] = useState('user');
  const [inviting, setInviting] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserType, setEditUserType] = useState('user');
  const [deletingUser, setDeletingUser] = useState(null);
  const [horizonManagementUser, setHorizonManagementUser] = useState(null);
  const [ownershipBlock, setOwnershipBlock] = useState(null); // { user, instances, message, remedy }
  const [deleteFailure, setDeleteFailure] = useState(null); // { user, message, remedy, details, referencedTable, referencedSuffix }
  const [takingOwnershipSuffix, setTakingOwnershipSuffix] = useState(null);

  useEffect(() => {
    if (user && (user.accountType === 'admin' || user.accountType === 'superadmin')) {
      loadUsers();
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/user-invites/list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({}),
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data.users || []);
      } else {
        if (response.status === 403) {
          setMessage({ type: 'error', text: 'You do not have permission to view users' });
        } else {
          setMessage({ type: 'error', text: 'Failed to load users' });
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setMessage({ type: 'error', text: 'Error loading users' });
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) {
      setMessage({ type: 'error', text: 'Email is required' });
      return;
    }

    try {
      setInviting(true);
      setMessage({ type: '', text: '' });

      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/user-invites/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          email: inviteEmail,
          user_type: inviteUserType,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: `Invitation sent to ${inviteEmail}` });
        setInviteEmail('');
        setInviteUserType('user');
        loadUsers();
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to send invitation' });
      }
    } catch (error) {
      console.error('Error inviting user:', error);
      setMessage({ type: 'error', text: 'Error sending invitation' });
    } finally {
      setInviting(false);
    }
  };

  const handleEdit = (userToEdit) => {
    setEditingUser(userToEdit);
    setEditUserType(userToEdit.user_type || 'user');
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    try {
      setMessage({ type: '', text: '' });
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/user-invites/update`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          user_id: editingUser.id,
          user_type: editUserType,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: `User ${editingUser.email} updated successfully` });
        setEditingUser(null);
        setEditUserType('user');
        loadUsers();
      } else {
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to update user' });
      }
    } catch (error) {
      console.error('Error updating user:', error);
      setMessage({ type: 'error', text: 'Error updating user' });
    }
  };

  const handleDelete = async (userToDelete) => {
    // Prevent self-deletion
    if (userToDelete.id === user.id) {
      setMessage({ type: 'error', text: 'You cannot delete yourself' });
      return;
    }

    // Prevent admins from deleting superadmins
    if (user.accountType !== 'superadmin' && userToDelete.user_type === 'superadmin') {
      setMessage({ type: 'error', text: 'Admins cannot delete superadmin users' });
      return;
    }

    if (!window.confirm(`Are you sure you want to delete user ${userToDelete.email}? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeletingUser(userToDelete.id);
      setMessage({ type: '', text: '' });
      setOwnershipBlock(null);
      setDeleteFailure(null);
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/user-invites/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: JSON.stringify({
          user_id: userToDelete.id,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setMessage({ type: 'success', text: `User ${userToDelete.email} deleted successfully` });
        setOwnershipBlock(null);
        setDeleteFailure(null);
        loadUsers();
      } else if (data.code === 'OWNS_ACTIVE_HORIZONS' || (data.ownedInstances && data.ownedInstances.length > 0)) {
        setOwnershipBlock({
          user: userToDelete,
          instances: data.ownedInstances || [],
          message: data.message || 'User owns horizons that must be reassigned first.',
          remedy: data.remedy || 'Take ownership below, then retry delete.',
        });
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to delete user' });
      } else {
        setDeleteFailure({
          user: userToDelete,
          code: data.code || null,
          message: data.message || data.error || 'Failed to delete user',
          remedy: data.remedy || null,
          details: data.details || data.rawMessage || null,
          referencedTable: data.referencedTable || null,
          referencedSuffix: data.referencedSuffix || null,
          referencedKind: data.referencedKind || null,
        });
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to delete user' });
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      setDeleteFailure({
        user: userToDelete,
        code: null,
        message: 'Error deleting user (network or unexpected failure).',
        remedy: 'Check the network tab / server logs, then retry.',
        details: error?.message || String(error),
        referencedTable: null,
        referencedSuffix: null,
        referencedKind: null,
      });
      setMessage({ type: 'error', text: 'Error deleting user' });
    } finally {
      setDeletingUser(null);
    }
  };

  const handleTakeOwnership = async (suffix) => {
    if (!user?.id || !suffix) return;
    try {
      setTakingOwnershipSuffix(suffix);
      setMessage({ type: '', text: '' });
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/casc/v1/api/instances/transfer-ownership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ suffix, new_owner_id: user.id }),
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setOwnershipBlock((prev) => {
          if (!prev) return null;
          const remaining = (prev.instances || []).filter((inst) => inst.suffix !== suffix);
          if (remaining.length === 0) return null;
          return { ...prev, instances: remaining };
        });
        setMessage({
          type: 'success',
          text: data.message || `Took ownership of "${suffix}". Retry delete when ready.`,
        });
      } else {
        setMessage({ type: 'error', text: data.message || data.error || `Failed to take ownership of ${suffix}` });
      }
    } catch (error) {
      console.error('Error taking ownership:', error);
      setMessage({ type: 'error', text: `Error taking ownership of ${suffix}` });
    } finally {
      setTakingOwnershipSuffix(null);
    }
  };

  const handleTakeOwnershipAll = async () => {
    if (!ownershipBlock?.instances?.length) return;
    const suffixes = ownershipBlock.instances.map((inst) => inst.suffix);
    for (const suffix of suffixes) {
      // Sequential so transfer errors stay readable
      // eslint-disable-next-line no-await-in-loop
      await handleTakeOwnership(suffix);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return dateString;
    }
  };

  if (!user || (user.accountType !== 'admin' && user.accountType !== 'superadmin')) {
    return (
      <div className="admin-settings-page">
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          You do not have permission to access admin settings.
        </div>
      </div>
    );
  }

  return (
    <div className="admin-settings-page">
      <h1>
        <i className="fas fa-user-shield"></i> Admin Settings
      </h1>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
          {message.text}
        </div>
      )}

      {ownershipBlock && (
        <div className="ownership-block-panel">
          <div className="ownership-block-header">
            <h3>
              <i className="fas fa-exclamation-triangle"></i>
              Cannot delete {ownershipBlock.user?.email}
            </h3>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setOwnershipBlock(null)}
            >
              Dismiss
            </button>
          </div>
          <p>{ownershipBlock.message}</p>
          {ownershipBlock.remedy && <p className="ownership-block-remedy">{ownershipBlock.remedy}</p>}
          <ul className="ownership-block-list">
            {ownershipBlock.instances.map((inst) => (
              <li key={inst.suffix}>
                <div className="ownership-block-instance">
                  <strong>{inst.suffix}</strong>
                  {inst.description ? <span> — {inst.description}</span> : null}
                  {inst.is_deleted ? <span className="deleted-pill">Deleted</span> : null}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => handleTakeOwnership(inst.suffix)}
                  disabled={takingOwnershipSuffix === inst.suffix || user?.accountType !== 'superadmin'}
                  title={
                    user?.accountType === 'superadmin'
                      ? 'Transfer this horizon to yourself'
                      : 'Only superadmins can take ownership'
                  }
                >
                  {takingOwnershipSuffix === inst.suffix ? (
                    <i className="fas fa-spinner fa-spin"></i>
                  ) : (
                    <>
                      <i className="fas fa-user-check"></i> Take ownership
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
          <div className="ownership-block-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleTakeOwnershipAll}
              disabled={
                !ownershipBlock.instances.length ||
                takingOwnershipSuffix != null ||
                user?.accountType !== 'superadmin'
              }
            >
              <i className="fas fa-user-check"></i> Take ownership of all
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => handleDelete(ownershipBlock.user)}
              disabled={
                ownershipBlock.instances.length > 0 ||
                deletingUser === ownershipBlock.user?.id
              }
              title={
                ownershipBlock.instances.length > 0
                  ? 'Take ownership of all listed horizons first'
                  : 'Retry deleting this user'
              }
            >
              {deletingUser === ownershipBlock.user?.id ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <>
                  <i className="fas fa-trash"></i> Retry delete
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {deleteFailure && (
        <div className="ownership-block-panel delete-failure-panel">
          <div className="ownership-block-header">
            <h3>
              <i className="fas fa-exclamation-triangle"></i>
              Delete failed for {deleteFailure.user?.email}
            </h3>
            <button
              type="button"
              className="btn btn-sm btn-secondary"
              onClick={() => setDeleteFailure(null)}
            >
              Dismiss
            </button>
          </div>
          <p>{deleteFailure.message}</p>
          {deleteFailure.remedy && <p className="ownership-block-remedy">{deleteFailure.remedy}</p>}
          {(deleteFailure.referencedSuffix || deleteFailure.referencedTable) && (
            <ul className="ownership-block-list">
              <li>
                <div className="ownership-block-instance">
                  {deleteFailure.referencedKind ? (
                    <strong>{deleteFailure.referencedKind}</strong>
                  ) : (
                    <strong>Referenced data</strong>
                  )}
                  {deleteFailure.referencedSuffix ? (
                    <span> in horizon <code>{deleteFailure.referencedSuffix}</code></span>
                  ) : null}
                  {deleteFailure.referencedTable ? (
                    <span className="ownership-block-remedy"> (table {deleteFailure.referencedTable})</span>
                  ) : null}
                </div>
              </li>
            </ul>
          )}
          {deleteFailure.details && (
            <pre className="delete-failure-details">{deleteFailure.details}</pre>
          )}
          <div className="ownership-block-actions">
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => handleDelete(deleteFailure.user)}
              disabled={deletingUser === deleteFailure.user?.id}
            >
              {deletingUser === deleteFailure.user?.id ? (
                <i className="fas fa-spinner fa-spin"></i>
              ) : (
                <>
                  <i className="fas fa-trash"></i> Retry delete
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <GoogleCredentialsPanel />

      {/* User Management Section */}
      <div className="settings-category">
        <h3>
          <i className="fas fa-users"></i> User Management
          <span className="badge badge-admin">ADMIN</span>
        </h3>

        {/* Add User Form */}
        <div className="invite-user-form">
          <h4>Add User</h4>
          <form onSubmit={handleInvite}>
            <div className="form-group">
              <label htmlFor="invite-email">Email Address</label>
              <input
                id="invite-email"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="user@example.com"
                required
                disabled={inviting}
              />
            </div>
            <div className="form-group">
              <label htmlFor="invite-user-type">User Type</label>
              <select
                id="invite-user-type"
                value={inviteUserType}
                onChange={(e) => setInviteUserType(e.target.value)}
                disabled={inviting || user.accountType !== 'superadmin'}
              >
                <option value="user">User</option>
                <option value="readonly">Read Only</option>
                {user.accountType === 'superadmin' && (
                  <>
                    <option value="admin">Admin</option>
                    <option value="superadmin">Superadmin</option>
                  </>
                )}
              </select>
              {user.accountType !== 'superadmin' && (
                <div className="form-hint">
                  Only superadmins can create admin and superadmin users
                </div>
              )}
            </div>
            <button type="submit" className="btn btn-primary" disabled={inviting}>
              {inviting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i> Sending...
                </>
              ) : (
                <>
                  <i className="fas fa-user-plus"></i> Add User
                </>
              )}
            </button>
          </form>
        </div>

        {/* Users List */}
        <div className="users-list">
          <h4>All Users</h4>
          {loading ? (
            <div className="loading">
              <i className="fas fa-spinner fa-spin"></i> Loading users...
            </div>
          ) : users.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-users"></i>
              <p>No users found</p>
            </div>
          ) : (
            <div className="users-table">
              <table>
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>User Type</th>
                    <th>Created</th>
                    <th>Last Updated</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id}>
                      {editingUser?.id === u.id ? (
                        <>
                          <td>{u.email}</td>
                          <td>
                            <select
                              value={editUserType}
                              onChange={(e) => setEditUserType(e.target.value)}
                              disabled={user.accountType !== 'superadmin' && (u.user_type === 'admin' || u.user_type === 'superadmin')}
                              className="edit-user-type-select"
                            >
                              <option value="user">User</option>
                              <option value="readonly">Read Only</option>
                              {user.accountType === 'superadmin' && (
                                <>
                                  <option value="admin">Admin</option>
                                  <option value="superadmin">Superadmin</option>
                                </>
                              )}
                            </select>
                          </td>
                          <td>{formatDate(u.created_at)}</td>
                          <td>{formatDate(u.updated_at)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={handleSaveEdit}
                            >
                              <i className="fas fa-save"></i> Save
                            </button>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => {
                                setEditingUser(null);
                                setEditUserType('user');
                              }}
                              style={{ marginLeft: '8px' }}
                            >
                              <i className="fas fa-times"></i> Cancel
                            </button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td>{u.email}</td>
                          <td>
                            <span className={`user-type-badge user-type-${u.user_type || 'user'}`}>
                              {u.user_type || 'user'}
                            </span>
                          </td>
                          <td>{formatDate(u.created_at)}</td>
                          <td>{formatDate(u.updated_at)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-secondary"
                              onClick={() => handleEdit(u)}
                              disabled={user.accountType !== 'superadmin' && (u.user_type === 'admin' || u.user_type === 'superadmin')}
                              title={user.accountType !== 'superadmin' && (u.user_type === 'admin' || u.user_type === 'superadmin') ? 'Only superadmins can edit admin users' : 'Edit user'}
                            >
                              <i className="fas fa-edit"></i> Edit
                            </button>
                            {user.accountType === 'superadmin' && (
                              <button
                                className="btn btn-sm btn-primary"
                                onClick={() => setHorizonManagementUser(u)}
                                style={{ marginLeft: '8px' }}
                                title="Manage user horizons (superadmin only)"
                              >
                                <i className="fas fa-database"></i> Horizons
                              </button>
                            )}
                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => handleDelete(u)}
                              disabled={
                                deletingUser === u.id || 
                                u.id === user.id || // Cannot delete yourself
                                (user.accountType !== 'superadmin' && (u.user_type === 'admin' || u.user_type === 'superadmin')) // Admins cannot delete admin/superadmin
                              }
                              title={
                                u.id === user.id 
                                  ? 'You cannot delete yourself' 
                                  : user.accountType !== 'superadmin' && (u.user_type === 'admin' || u.user_type === 'superadmin')
                                    ? 'Only superadmins can delete admin users' 
                                    : 'Delete user'
                              }
                              style={{ marginLeft: '8px' }}
                            >
                              {deletingUser === u.id ? (
                                <i className="fas fa-spinner fa-spin"></i>
                              ) : (
                                <i className="fas fa-trash"></i>
                              )}
                              {' '}Delete
                            </button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Horizon Management Modal */}
      {horizonManagementUser && (
        <HorizonManagementModal
          user={horizonManagementUser}
          onClose={() => setHorizonManagementUser(null)}
          onUpdate={() => {
            // Optionally reload users or update UI
          }}
        />
      )}
    </div>
  );
}

export default AdminSettingsPage;
